import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Shield, Clock, CheckCircle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CalendarSyncPromptProps {
  eventId: string;
  guestId: string;
  onSyncComplete?: (provider: string) => void;
  onDismiss?: () => void;
  maxConnections?: number;
  currentConnections?: number;
}

export const CalendarSyncPrompt = ({ 
  eventId, 
  guestId, 
  onSyncComplete, 
  onDismiss,
  maxConnections = 3,
  currentConnections = 0
}: CalendarSyncPromptProps) => {
  const { toast } = useToast();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    if (currentConnections >= maxConnections) {
      toast({
        title: "Connection limit reached",
        description: `You can connect up to ${maxConnections} calendars. Upgrade to Pro for unlimited connections.`,
        variant: "destructive",
      });
      return;
    }

    setConnecting(provider);
    try {
      // Get OAuth URL from our edge function
      console.log('Calling calendar-oauth function with:', {
        action: 'get_auth_url',
        provider: provider,
        redirect_uri: `${window.location.origin}/calendar-callback`,
        event_id: eventId,
        guest_id: guestId,
      });

      const { data: urlData, error: urlError } = await supabase.functions.invoke('calendar-oauth', {
        body: {
          action: 'get_auth_url',
          provider: provider,
          redirect_uri: `${window.location.origin}/calendar-callback`,
          event_id: eventId,
          guest_id: guestId,
        }
      });

      console.log('Edge function response:', { urlData, urlError });

      if (urlError || !urlData?.auth_url) {
        console.error('Failed to get auth URL:', urlError);
        console.error('URL data received:', urlData);
        throw new Error(urlError?.message || urlData?.error || 'Failed to get authorization URL');
      }

      // Open OAuth flow in a popup
      const popup = window.open(
        urlData.auth_url,
        'calendar-oauth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      // Listen for OAuth completion
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'CALENDAR_OAUTH_SUCCESS') {
          popup?.close();
          window.removeEventListener('message', handleMessage);
          
          toast({
            title: "Calendar connected successfully",
            description: `Your ${provider} calendar has been synced. We'll check your availability for this event.`,
          });
          
          onSyncComplete?.(provider);
          setConnecting(null);
        } else if (event.data.type === 'CALENDAR_OAUTH_ERROR') {
          popup?.close();
          window.removeEventListener('message', handleMessage);
          throw new Error(event.data.error || 'OAuth flow failed');
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was blocked
      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site and try again.');
      }

      // Handle popup being closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setConnecting(null);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast({
        title: "Connection failed",
        description: error instanceof Error ? error.message : "Failed to connect your calendar. Please try again later.",
        variant: "destructive",
      });
      setConnecting(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Check Your Availability
              <Badge variant="secondary" className="text-xs">Smart Sync</Badge>
            </CardTitle>
            <CardDescription>
              Connect your calendar to automatically check your availability and vote more accurately.
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDismiss}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Benefits */}
        <div className="grid gap-2">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>See which options you're actually free for</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-blue-600" />
            <span>Vote faster with smart suggestions</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-purple-600" />
            <span>Read-only access, your privacy protected</span>
          </div>
        </div>

        {/* Connection Status */}
        {currentConnections > 0 && (
          <div className="text-sm text-muted-foreground">
            You have {currentConnections} calendar{currentConnections !== 1 ? 's' : ''} connected
          </div>
        )}

        {/* Connection Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => handleConnect('google')}
            disabled={connecting !== null}
            className="flex-1"
            variant="outline"
          >
            {connecting === 'google' ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Calendar
              </>
            )}
          </Button>
          
          <Button
            onClick={() => handleConnect('microsoft')}
            disabled={connecting !== null}
            className="flex-1"
            variant="outline"
          >
            {connecting === 'microsoft' ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                </svg>
                Microsoft Outlook
              </>
            )}
          </Button>
        </div>

        {/* Limitation Notice */}
        {maxConnections < 999 && (
          <div className="text-xs text-muted-foreground p-2 bg-muted rounded">
            <strong>Free users:</strong> Up to {maxConnections} calendar connections per event. 
            <span className="text-primary cursor-pointer hover:underline ml-1">
              Upgrade to Pro for unlimited connections
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};